import type { ReactNode } from 'react';
import { Box, Card, Container, Group, Stack, Text } from '@mantine/core';
import { IconPackage } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../../shared/i18n/LanguageSwitcher';
import { AppFooter } from '../../app/AppFooter';

interface Props {
  /** Omitted by the invitation screen, which is one step rather than three. */
  step?: 'step1Of3' | 'step2Of3' | 'step3Of3';
  children: ReactNode;
}

/**
 * Shared chrome for the signed-out account screens. Renders:
 *   - The InveTrak brand mark
 *   - The "step N of 3" pill, where there is a sequence to be in
 *   - The language switcher (top-right of the page)
 *   - A white card that wraps the per-step body
 *
 * Keeps the per-route components focused on their own form logic. Accepting an invitation borrows
 * it too: it is the same situation — somebody signed out, on a link from an email, setting a
 * password — and it arrives without a step, because it is the only one.
 */
export function RegistrationLayout({ step, children }: Props) {
  const { t } = useTranslation();
  return (
    <Box
      mih="100vh"
      bg="var(--mantine-color-gray-0)"
      py="xl"
    >
      <Container size={520}>
        <Stack gap="lg">
          <Group justify="space-between" align="flex-end">
            <Group gap="sm" align="center">
              <Box
                w={32}
                h={32}
                style={{
                  borderRadius: 8,
                  background: 'var(--mantine-color-invetrak-1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IconPackage size={18} color="var(--mantine-color-invetrak-7)" />
              </Box>
              <Text fw={600} size="md">{t('app.name')}</Text>
            </Group>
            <LanguageSwitcher />
          </Group>

          {step && (
            <Group gap="xs">
              <Text
                size="xs"
                fw={500}
                c="invetrak.7"
                bg="invetrak.0"
                px="sm"
                py={4}
                style={{ borderRadius: 8 }}
              >
                {t(`registration.${step}`)}
              </Text>
            </Group>
          )}

          <Card withBorder radius="md" padding="xl" shadow="none">
            {children}
          </Card>
        </Stack>
      </Container>

      <AppFooter variant="transparent" />
    </Box>
  );
}
